import { useId, useState, type CSSProperties } from 'react'
import clsx from 'clsx'

import type { ISODate } from '@app/shared'
import type { ChatThread } from '../../lib/data'
import { anchorAtPoint } from '../../lib/popover'
import { ContextMenu, type MenuAnchor } from '../ContextMenu'
import { Search } from '../icons'
import { Unavailable } from '../interests/parts'
import { Plus } from './icons'
import { ScopePill } from './parts'
import { groupOf, messageCountLine, THREAD_GROUPS } from './labels'

// ============================================================================
// THE THREAD LIST — LifeOS Chat.dc.html, options 11a and 11b, the 262px column.
//
// README §10: "`New` button (dark pill, radius 9, `chat-lift` violet plus icon),
// a `Search threads` field on `surface-4`, then threads grouped `TODAY` /
// `THIS WEEK` / `EARLIER` (600 9.5px, 0.08em, `microcopy`). Each row: title at
// 500–600 12.5px, a scope pill, and a message count. Active row: `fill`."
// (Token names, not the README's literals: no raw hex may appear anywhere in
// apps/web/src outside index.css — the ship gate's brand audit greps for it, and
// a hex in a comment drifts from the token exactly as fast as one in a class.)
//
// THE GROUPING IS DERIVED, never stored: a thread's heading is a fact about the
// app's clock and its `updatedAt`, and the demo clock is fixed so the same thread
// lands under the same heading in every screenshot forever.
//
// RIGHT-CLICK IS THE ONLY WAY TO DELETE A THREAD, and that is a design decision
// rather than an oversight. §10 draws this row with exactly three things in it —
// a title, a scope pill and a message count — so a `⋯` button would be a fourth
// the handoff does not have, on the one column that is visible from every screen
// in the app. The row keeps its drawing; the pointer gains a menu.
//
// WHICH MEANS THE PHONE CANNOT DELETE A THREAD YET. That is stated rather than
// papered over: the mobile shell (src/mobile) does not render this column at all,
// so nothing there has lost a control it used to have, and the day it does render
// threads is the day this row needs a long-press or a visible trigger.
//
// THE SEARCH FIELD IS REAL and it is UNCONTROLLED. The value lives in the DOM,
// not in React, so the browser's own undo stack survives — the same rule the
// journal composer is built on (docs/design/FIDELITY.md → what no gate can see).
// The filter is a plain substring match over titles: no server round trip, no
// debounce to tune, and nothing that could claim to have searched the messages,
// which it has not.
// ============================================================================

export function ThreadList({
  threads,
  today,
  activeThreadId,
  isNewThread,
  isPending,
  error,
  onSelect,
  onNew,
  onDelete,
  deleteError,
}: {
  threads: ChatThread[] | undefined
  today: ISODate | undefined
  activeThreadId: string | undefined
  /** True while the composer is a fresh thread that has not been sent yet (11b). */
  isNewThread: boolean
  isPending: boolean
  error: unknown
  onSelect: (threadId: string) => void
  onNew: () => void
  /** Confirmed already — by the time this fires the owner has said yes once. */
  onDelete: (threadId: string) => void
  /** A delete that did not happen. Shown above the list; silence here would look like a no-op. */
  deleteError?: unknown
}) {
  const searchId = useId()
  const [query, setQuery] = useState('')
  // ONE MENU, OWNED BY THE LIST rather than by a row, for the reason the task board gives: a row
  // only knows about itself, and "only one is open" is a fact about all of them. (The primitive
  // enforces it across components too — this keeps the state in one place.)
  const [menu, setMenu] = useState<{ thread: ChatThread; anchor: MenuAnchor } | null>(null)

  const needle = query.trim().toLowerCase()
  const rows = (threads ?? []).filter((t) => t.title.toLowerCase().includes(needle))

  // Only the groups that actually have rows, so a heading never stands over nothing and the
  // 14px gap above a heading can be decided by position rather than by a CSS pseudo-class that
  // `display:contents` would evaluate against the wrong parent.
  const groups = today
    ? THREAD_GROUPS.map((group) => ({
        group,
        rows: rows.filter((t) => groupOf(t, today) === group),
      })).filter((g) => g.rows.length > 0)
    : []

  return (
    <div className="flex w-[262px] flex-none flex-col border-r border-line bg-surface">
      <div className="flex flex-none flex-col gap-3 px-[18px] pb-[13px] pt-[18px]">
        <div className="flex items-center gap-[9px]">
          <h1 className="text-[14px] font-semibold leading-none tracking-[-0.01em]">Chat</h1>
          <button
            type="button"
            onClick={onNew}
            className="ml-auto flex items-center gap-[6px] rounded-button bg-ink px-[10px] py-[5px] text-[11px] font-semibold leading-none text-surface transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-ink-2"
          >
            <Plus className="text-chat-lift" />
            New
          </button>
        </div>

        <div className="flex items-center gap-[9px] rounded-button bg-surface-4 px-[11px] py-2">
          <Search size={13} strokeWidth={1.9} className="flex-none text-microcopy" />
          <label htmlFor={searchId} className="sr-only">
            Search threads by title
          </label>
          <input
            id={searchId}
            type="text"
            autoComplete="off"
            placeholder="Search threads"
            onChange={(e) => setQuery(e.currentTarget.value)}
            className="min-w-0 flex-1 bg-transparent text-[11.5px] leading-none text-ink outline-none placeholder:text-microcopy"
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-[3px] overflow-y-auto px-3 pb-4">
        {/* A delete that failed. Above the rows, because the row it was about is still one of
            them — the list is telling you why it did not change. */}
        {deleteError !== undefined && deleteError !== null && (
          <Unavailable error={deleteError} className="mb-2" />
        )}

        {/* The unsent thread pins itself to the top of the list, above TODAY — 11b draws it
            there so the list never looks like it forgot what you are typing into. */}
        {isNewThread && (
          <div className="flex items-center gap-[9px] rounded-tile bg-fill px-[11px] py-[10px]">
            <span className="text-[12.5px] font-semibold leading-[1.3] text-muted">New thread</span>
            <span className="ml-auto text-[10px] leading-none text-microcopy">now</span>
          </div>
        )}

        {error ? (
          <Unavailable error={error} className="mt-2" />
        ) : isPending ? (
          <div className="mt-2 flex flex-col gap-[3px]" aria-hidden>
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="h-[52px] animate-pulse rounded-tile bg-surface-4" />
            ))}
            <span className="sr-only">Loading…</span>
          </div>
        ) : rows.length === 0 ? (
          <p className="px-2 pt-3 text-[11px] leading-[1.45] text-microcopy text-pretty">
            {needle
              ? `No thread is called “${query.trim()}”. The search reads thread titles only — the messages inside them are not indexed yet.`
              : 'No threads yet. The New button starts one.'}
          </p>
        ) : (
          groups.map(({ group, rows: inGroup }, i) => (
            <section key={group} className="contents">
              <h2
                className={clsx(
                  'px-2 pb-2 text-[9.5px] font-semibold leading-none tracking-[0.08em] text-microcopy',
                  // 6px above the first heading, 14px above every later one — the mockup's own
                  // two paddings, which is what keeps EARLIER from crowding the row above it.
                  i === 0 && !isNewThread ? 'pt-[6px]' : 'pt-[14px]',
                )}
              >
                {group}
              </h2>
              {inGroup.map((thread) => (
                <ThreadRow
                  key={thread.id}
                  thread={thread}
                  active={!isNewThread && thread.id === activeThreadId}
                  onSelect={() => onSelect(thread.id)}
                  onMenu={(anchor) => setMenu({ thread, anchor })}
                />
              ))}
            </section>
          ))
        )}
      </div>

      {menu && (
        <ThreadMenu
          key={menu.thread.id}
          thread={menu.thread}
          anchor={menu.anchor}
          onDelete={() => {
            onDelete(menu.thread.id)
            setMenu(null)
          }}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  )
}

const MENU_WIDTH = 216

/**
 * THE THREAD MENU — one action, and it asks first.
 *
 * A one-item menu is not a waste of a menu: the item is irreversible, and the alternative shapes
 * are worse. A bare `Delete` in the row would be a mis-click away from a conversation that is
 * gone; a `window.confirm` would steal the whole window for a sentence this panel has room for,
 * and would not be able to say how many messages go with it.
 *
 * THE COUNT IS THE THREAD'S OWN. It is what the row under the pointer already prints, so the
 * confirmation cannot claim a number the list disagrees with.
 */
function ThreadMenu({
  thread,
  anchor,
  onDelete,
  onClose,
}: {
  thread: ChatThread
  anchor: MenuAnchor
  onDelete: () => void
  onClose: () => void
}) {
  const [confirming, setConfirming] = useState(false)

  return (
    <ContextMenu
      anchor={anchor}
      label={`Actions for ${thread.title}`}
      width={MENU_WIDTH}
      onClose={onClose}
    >
      <div className="truncate text-[11.5px] font-semibold leading-[1.3] text-ink">
        {thread.title}
      </div>

      {confirming ? (
        <div className="flex flex-col gap-[7px]">
          <p className="text-[10.5px] leading-[1.4] text-danger-2 text-pretty">
            Delete this thread? Its {messageCountLine(thread.messageCount)}{' '}
            {thread.messageCount === 1 ? 'goes' : 'go'} with it, and there is no undo.
          </p>
          <div className="flex items-center gap-[10px]">
            <button
              type="button"
              onClick={onDelete}
              className="rounded-context border border-danger-line bg-danger-fill px-[10px] py-[5px] text-[11px] font-semibold leading-none text-danger-2"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="text-[11px] font-medium leading-none text-ink-3 hover:text-ink"
            >
              Keep it
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="self-start text-[11px] font-semibold leading-none text-danger-2"
        >
          Delete thread
        </button>
      )}
    </ContextMenu>
  )
}

function ThreadRow({
  thread,
  active,
  onSelect,
  onMenu,
}: {
  thread: ChatThread
  active: boolean
  onSelect: () => void
  onMenu: (anchor: MenuAnchor) => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      // Suppressed on this row and nowhere else — the app replaces the native menu only where it
      // has something to put in its place (components/ContextMenu.tsx).
      onContextMenu={(e) => {
        e.preventDefault()
        onMenu(anchorAtPoint(e.clientX, e.clientY))
      }}
      aria-current={active ? 'true' : undefined}
      style={{ '--mo-rule': 'var(--color-chat)' } as CSSProperties}
      className={clsx(
        'mo-rule flex w-full flex-col items-start gap-[6px] rounded-tile px-[11px] py-[10px] text-left',
        active ? 'bg-fill' : '',
      )}
    >
      <span
        className={clsx(
          'text-[12.5px] leading-[1.3]',
          active ? 'font-semibold text-ink' : 'font-medium text-ink-2',
        )}
      >
        {thread.title}
      </span>
      <span className="flex items-center gap-[7px]">
        <ScopePill scopeId={thread.scopeId} label={thread.scopeLabel} onLift={active} />
        <span className="text-[10px] leading-none text-microcopy">
          {messageCountLine(thread.messageCount)}
        </span>
      </span>
    </button>
  )
}
